import { BigNumber } from '@ethersproject/bignumber'
import { Contract } from '@ethersproject/contracts'
import { JSBI, Percent, Router, SwapParameters, Trade, TradeType, currencyEquals } from '@uniswap/sdk'
import { useMemo } from 'react'
import i18n from '../i18n'
import { BIPS_BASE, DEFAULT_DEADLINE_FROM_NOW, INITIAL_ALLOWED_SLIPPAGE, WETH_EXTENDED } from '../constants'
import { useTransactionAdder } from '../state/transactions/hooks'
import { calculateGasMargin, getRouterContract, isAddress, shortenAddress } from '../utils'
import isZero from '../utils/isZero'
import { useActiveWeb3React } from './index'
import useENS from './useENS'

// Custom swap call parameters that handles wrapped native currency (WETH) as if it were ETH
function customSwapCallParameters(
  trade: Trade,
  options: {
    feeOnTransfer: boolean
    allowedSlippage: Percent
    recipient: string
    ttl: number
  },
  chainId: number
): SwapParameters {
  const weth = WETH_EXTENDED[chainId as keyof typeof WETH_EXTENDED]
  if (!weth) {
    // Fallback to default Router behavior
    return Router.swapCallParameters(trade, options)
  }

  // Check if input or output is WETH (which represents native currency on custom chains)
  const inputIsWETH = currencyEquals(trade.inputAmount.currency, weth)
  const outputIsWETH = currencyEquals(trade.outputAmount.currency, weth)

  // If neither input nor output is WETH, use default Router behavior
  if (!inputIsWETH && !outputIsWETH) {
    return Router.swapCallParameters(trade, options)
  }

  const etherIn = inputIsWETH
  const etherOut = outputIsWETH

  const to = options.recipient
  const amountIn = trade.maximumAmountIn(options.allowedSlippage).raw.toString()
  const amountOut = trade.minimumAmountOut(options.allowedSlippage).raw.toString()
  const path = trade.route.path.map(token => token.address)
  const deadline = `0x${(Math.floor(Date.now() / 1000) + options.ttl).toString(16)}`

  const useFeeOnTransfer = options.feeOnTransfer

  let methodName: string
  let args: (string | string[])[]
  let value: string

  if (trade.tradeType === TradeType.EXACT_INPUT) {
    if (etherIn) {
      methodName = useFeeOnTransfer ? 'swapExactETHForTokensSupportingFeeOnTransferTokens' : 'swapExactETHForTokens'
      args = [amountOut, path, to, deadline]
      value = amountIn
    } else if (etherOut) {
      methodName = useFeeOnTransfer ? 'swapExactTokensForETHSupportingFeeOnTransferTokens' : 'swapExactTokensForETH'
      args = [amountIn, amountOut, path, to, deadline]
      value = '0x0'
    } else {
      methodName = useFeeOnTransfer
        ? 'swapExactTokensForTokensSupportingFeeOnTransferTokens'
        : 'swapExactTokensForTokens'
      args = [amountIn, amountOut, path, to, deadline]
      value = '0x0'
    }
  } else {
    // EXACT_OUTPUT
    if (etherIn) {
      methodName = 'swapETHForExactTokens'
      args = [amountOut, path, to, deadline]
      value = amountIn
    } else if (etherOut) {
      methodName = 'swapTokensForExactETH'
      args = [amountOut, amountIn, path, to, deadline]
      value = '0x0'
    } else {
      methodName = 'swapTokensForExactTokens'
      args = [amountOut, amountIn, path, to, deadline]
      value = '0x0'
    }
  }

  return { methodName, args, value }
}

export enum SwapCallbackState {
  INVALID,
  LOADING,
  VALID
}

interface SwapCall {
  contract: Contract
  parameters: SwapParameters
}

interface SuccessfulCall {
  call: SwapCall
  gasEstimate: BigNumber
}

interface FailedCall {
  call: SwapCall
  error: Error
}

type EstimatedSwapCall = SuccessfulCall | FailedCall

/**
 * Returns the swap calls that can be used to make the trade
 * @param trade trade to execute
 * @param allowedSlippage user allowed slippage
 * @param deadline the deadline for the trade
 * @param recipientAddressOrName
 */
function useSwapCallArguments(
  trade: Trade | undefined, // trade to execute, required
  allowedSlippage: number = INITIAL_ALLOWED_SLIPPAGE, // in bips
  deadline: number = DEFAULT_DEADLINE_FROM_NOW, // in seconds from now
  recipientAddressOrName: string | null // the ENS name or address of the recipient of the trade, or null if swap should be returned to sender
): SwapCall[] {
  const { account, chainId, library } = useActiveWeb3React()

  const { address: recipientAddress } = useENS(recipientAddressOrName)
  const recipient = recipientAddressOrName === null ? account : recipientAddress

  return useMemo(() => {
    if (!trade || !recipient || !library || !account || !chainId) return []

    const contract: Contract | null = getRouterContract(chainId, library, account)
    if (!contract) {
      return []
    }

    const swapMethods = []

    swapMethods.push(
      customSwapCallParameters(
        trade,
        {
          feeOnTransfer: false,
          allowedSlippage: new Percent(JSBI.BigInt(allowedSlippage), BIPS_BASE),
          recipient,
          ttl: deadline
        },
        chainId
      )
    )

    if (trade.tradeType === TradeType.EXACT_INPUT) {
      swapMethods.push(
        customSwapCallParameters(
          trade,
          {
            feeOnTransfer: true,
            allowedSlippage: new Percent(JSBI.BigInt(allowedSlippage), BIPS_BASE),
            recipient,
            ttl: deadline
          },
          chainId
        )
      )
    }

    return swapMethods.map(parameters => ({ parameters, contract }))
  }, [account, allowedSlippage, chainId, deadline, library, recipient, trade])
}

// returns a function that will execute a swap, if the parameters are all valid
// and the user has approved the slippage adjusted input amount for the trade
export function useSwapCallback(
  trade: Trade | undefined, // trade to execute, required
  allowedSlippage: number = INITIAL_ALLOWED_SLIPPAGE, // in bips
  deadline: number = DEFAULT_DEADLINE_FROM_NOW, // in seconds from now
  recipientAddressOrName: string | null // the ENS name or address of the recipient of the trade, or null if swap should be returned to sender
): { state: SwapCallbackState; callback: null | (() => Promise<string>); error: string | null } {
  const { account, chainId, library } = useActiveWeb3React()

  const swapCalls = useSwapCallArguments(trade, allowedSlippage, deadline, recipientAddressOrName)

  const addTransaction = useTransactionAdder()

  const { address: recipientAddress } = useENS(recipientAddressOrName)
  const recipient = recipientAddressOrName === null ? account : recipientAddress

  return useMemo(() => {
    if (!trade || !library || !account || !chainId) {
      return { state: SwapCallbackState.INVALID, callback: null, error: i18n.t('missingDependencies') as string }
    }
    if (!recipient) {
      if (recipientAddressOrName !== null) {
        return { state: SwapCallbackState.INVALID, callback: null, error: i18n.t('invalidRecipient') as string }
      } else {
        return { state: SwapCallbackState.LOADING, callback: null, error: null }
      }
    }

    return {
      state: SwapCallbackState.VALID,
      callback: async function onSwap(): Promise<string> {
        const estimatedCalls: EstimatedSwapCall[] = await Promise.all(
          swapCalls.map(call => {
            const {
              parameters: { methodName, args, value },
              contract
            } = call
            const options = !value || isZero(value) ? {} : { value }

            return contract.estimateGas[methodName](...args, options)
              .then(gasEstimate => {
                return {
                  call,
                  gasEstimate
                }
              })
              .catch(gasError => {
                console.debug('Gas estimate failed, trying eth_call to extract error', call)

                return contract.callStatic[methodName](...args, options)
                  .then(result => {
                    console.debug('Unexpected successful call after failed estimate gas', call, gasError, result)
                    return { call, error: new Error(i18n.t('unexpectedGasEstimateError')) }
                  })
                  .catch(callError => {
                    console.debug('Call threw error', call, callError)
                    let errorMessage: string
                    switch (callError.reason) {
                      case 'UniswapV2Router: INSUFFICIENT_OUTPUT_AMOUNT':
                      case 'UniswapV2Router: EXCESSIVE_INPUT_AMOUNT':
                        errorMessage = i18n.t('swapPriceMovementError')
                        break
                      default:
                        errorMessage = i18n.t('swapTransactionError', { reason: callError.reason })
                    }
                    return { call, error: new Error(errorMessage) }
                  })
              })
          })
        )

        // a successful estimation is a bignumber gas estimate and the next call is also a bignumber gas estimate
        const successfulEstimation = estimatedCalls.find(
          (el, ix, list): el is SuccessfulCall =>
            'gasEstimate' in el && (ix === list.length - 1 || 'gasEstimate' in list[ix + 1])
        )

        if (!successfulEstimation) {
          const errorCalls = estimatedCalls.filter((call): call is FailedCall => 'error' in call)
          if (errorCalls.length > 0) throw errorCalls[errorCalls.length - 1].error
          throw new Error('Unexpected error. Please contact support: none of the calls threw an error')
        }

        const {
          call: {
            contract,
            parameters: { methodName, args, value }
          },
          gasEstimate
        } = successfulEstimation

        return contract[methodName](...args, {
          gasLimit: calculateGasMargin(gasEstimate),
          ...(value && !isZero(value) ? { value, from: account } : { from: account })
        })
          .then((response: any) => {
            const inputSymbol = trade.inputAmount.currency.symbol
            const outputSymbol = trade.outputAmount.currency.symbol
            const inputAmount = trade.inputAmount.toSignificant(3)
            const outputAmount = trade.outputAmount.toSignificant(3)

            const base = i18n.t('swapSummary', { inputAmount, inputSymbol, outputAmount, outputSymbol })
            const withRecipient =
              recipient === account
                ? base
                : i18n.t('swapSummaryTo', {
                    inputAmount,
                    inputSymbol,
                    outputAmount,
                    outputSymbol,
                    recipient:
                      recipientAddressOrName && isAddress(recipientAddressOrName)
                        ? shortenAddress(recipientAddressOrName)
                        : recipientAddressOrName
                  })

            addTransaction(response, {
              summary: withRecipient
            })

            return response.hash
          })
          .catch((error: any) => {
            // if the user rejected the tx, pass this along
            if (error?.code === 4001) {
              throw new Error(i18n.t('transactionRejected'))
            } else {
              // otherwise, the error was unexpected and we need to convey that
              console.error(`Swap failed`, error, methodName, args, value)
              throw new Error(i18n.t('swapFailed', { message: error.message }))
            }
          })
      },
      error: null
    }
  }, [trade, library, account, chainId, recipient, recipientAddressOrName, swapCalls, addTransaction])
}
